import React, { useEffect, useState } from "react";
import {
  PLUGIN_TYPE_LABELS,
  PluginType,
} from "../../../../domain/types/kpi";
import type { CreatePluginRequest } from "../../../../domain/types/kpi";
import {
  CATEGORY_PERIOD_DURATION_OPTIONS,
  type CategoryPeriodDuration,
  addYears,
  durationToYears,
} from "./category-period-utils";

/**
 * 카테고리(사업 참여) 추가 모달.
 *
 * 백엔드 `POST /api/admin/kpi/plugin` 요청 페이로드 (companyId 제외) 를 부모에
 * 그대로 전달한다. 부모(`ManageRegisteredCompanies2`) 가 companyId 를 주입해
 * `adminKpiApiService.createPlugin` 을 호출한다.
 */
interface CategoryAddModalProps {
  onClose: () => void;
  onSubmit: (
    request: Omit<CreatePluginRequest, "companyId">,
  ) => Promise<void> | void;
  /** 이미 참여 중인 사업 코드 목록 (중복 방지) */
  existingProgramCodes: PluginType[];
}

const CODE_OPTIONS: { label: string; value: PluginType }[] = [
  {
    label: PLUGIN_TYPE_LABELS[PluginType.SMART_WORKSHOP],
    value: PluginType.SMART_WORKSHOP,
  },
  {
    label: PLUGIN_TYPE_LABELS[PluginType.SMART_FACTORY],
    value: PluginType.SMART_FACTORY,
  },
  {
    label: PLUGIN_TYPE_LABELS[PluginType.ACHIEVEMENT_NOTICE],
    value: PluginType.ACHIEVEMENT_NOTICE,
  },
];

const CategoryAddModal: React.FC<CategoryAddModalProps> = ({
  onClose,
  onSubmit,
  existingProgramCodes,
}) => {
  // 이미 참여한 사업(스마트 공방/공장)은 선택지에서 제외 — 각 사업은 회사당 1번.
  const availableOptions = CODE_OPTIONS.filter(
    (o) => !existingProgramCodes.includes(o.value),
  );
  const [pluginType, setPluginType] = useState<PluginType>(
    availableOptions[0]?.value ?? PluginType.SMART_WORKSHOP,
  );
  const [certKey, setCertKey] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // 기본 기간 1년 — 사용자가 시작일을 입력하면 자동으로 만료일이 1년 뒤로 채워짐
  const [duration, setDuration] = useState<CategoryPeriodDuration>("1Y");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 지원사업 고지 문구 — KPI·날짜·인증키 없이 자유 텍스트만 입력.
  const isNotice = pluginType === PluginType.ACHIEVEMENT_NOTICE;
  // 공방/공장 둘 다 인증키 필수 — 가입 시 최초 1회만 입력 가능하고 이후 수정 API가 없어
  // (오입력 시 연동 해제 후 재가입), 여기서 안 받으면 다시 넣을 방법이 없다.
  const needsKey =
    pluginType === PluginType.SMART_WORKSHOP ||
    pluginType === PluginType.SMART_FACTORY;
  const isCustomDuration = duration === "CUSTOM";

  // 시작일 변경 핸들러 — duration 이 N년 옵션이면 만료일 자동 재계산
  const handleStartDateChange = (next: string) => {
    setStartDate(next);
    const years = durationToYears(duration);
    if (years !== null) {
      setEndDate(addYears(next, years));
    }
  };

  // 기간 select 변경 — N년 옵션이면 시작일 기준 만료일 재계산, CUSTOM 이면 만료일 칸 활성화 (값은 유지)
  const handleDurationChange = (next: CategoryPeriodDuration) => {
    setDuration(next);
    const years = durationToYears(next);
    if (years !== null) {
      setEndDate(addYears(startDate, years));
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const handleSubmit = async () => {
    if (existingProgramCodes.includes(pluginType)) {
      alert(`${PLUGIN_TYPE_LABELS[pluginType]} 사업에 이미 참여 중입니다.`);
      return;
    }
    if (needsKey && !certKey.trim()) {
      alert("인증키를 입력해주세요.");
      return;
    }
    // 모든 사업(고지 포함) 기간 입력 필수 — notice 도 공방/공장과 동일하게 날짜를 받는다.
    // (고지 문구는 추가 후 카테고리 탭에서 작성)
    if (!startDate || !endDate) {
      alert("시작 일자와 만료 일자를 모두 입력해주세요.");
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({
        pluginType,
        startDate,
        endDate,
        certKey: needsKey ? certKey.trim() : null,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[200000] flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[12px] w-[460px] max-w-[calc(100vw-32px)] p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-[16px] font-bold mb-1">카테고리 추가</h3>
        <p className="text-[12.5px] text-[#888] mb-5">
          카테고리 탭을 활성화하려면 아래 정보를 입력하세요.
        </p>

        <label className="text-[12.5px] text-[#555] block mb-1.5">카테고리</label>
        <select
          value={pluginType}
          onChange={(e) => setPluginType(e.target.value as PluginType)}
          className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white mb-4 w-full"
        >
          {availableOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        {needsKey && (
          <div>
            <label className="text-[12.5px] text-[#555] block mb-1.5">
              {pluginType === PluginType.SMART_WORKSHOP
                ? "KPI 인증키"
                : "로그 API 인증키"}{" "}
              <span className="text-[#F24A4A]">*</span>
            </label>
            <input
              type="text"
              value={certKey}
              onChange={(e) => setCertKey(e.target.value)}
              placeholder={
                pluginType === PluginType.SMART_WORKSHOP
                  ? "xxxx-xxxx-xxxx-xxxx"
                  : "사업관리시스템 발급 인증키"
              }
              className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white w-full mb-1.5 font-mono"
            />
            <p className="text-[11.5px] text-[#888] mb-4">
              가입 시 최초 1회만 입력 가능 · 이후 수정 불가(오입력 시 연동 해제 후 재가입)
            </p>
          </div>
        )}

        {isNotice && (
          <p className="text-[12.5px] text-[#555] bg-[#F5F8FF] border border-[#4285F4]/20 rounded-lg px-3 py-2.5 mb-4">
            기간을 입력해 추가하면 카테고리 탭이 생성됩니다. 고지 문구는 그 탭에서 작성·수정하세요.
          </p>
        )}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div>
            <label className="text-[12.5px] text-[#555] block mb-1.5">
              시작 일자 <span className="text-[#F24A4A]">*</span>
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white w-full"
            />
          </div>
          <div>
            <label className="text-[12.5px] text-[#555] block mb-1.5">기간</label>
            <select
              value={duration}
              onChange={(e) =>
                handleDurationChange(e.target.value as CategoryPeriodDuration)
              }
              className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white w-full"
            >
              {CATEGORY_PERIOD_DURATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[12.5px] text-[#555] block mb-1.5">
              만료 일자 <span className="text-[#F24A4A]">*</span>
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={!isCustomDuration}
              className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white w-full disabled:bg-[#F5F5F5] disabled:text-[#BBBBBB] disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-white text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB] disabled:opacity-60"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="inline-flex items-center justify-center font-medium rounded-lg px-4 py-2 text-[13px] bg-[#252525] text-white hover:bg-[#333] disabled:opacity-60"
          >
            {isSubmitting ? "등록 중…" : "등록"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryAddModal;